<template>
  <div>
    <v-dialog
      v-model="dialogOpen"
      :persistent="viewerOpen"
      max-width="900"
      content-class="public-record-dialog"
    >
      <v-card
        v-if="value && record"
        class="public-record-card"
      >
        <header class="public-record-header">
          <div class="public-record-owner">
            <v-avatar size="40">
              <v-img
                :src="ownerAvatar || defaultAvatar"
                :alt="ownerName"
              ></v-img>
            </v-avatar>
            <span>{{ ownerName }}</span>
          </div>
          <div class="public-record-heading">
            <h2 :title="displayName || $t(&quot;Memory&quot;)">
              {{ displayName || $t("Memory") }}
            </h2>
            <small
              class="text--secondary"
            >{{ $t("\n              Memory Bit") }}<span v-if="memoryDate"> / {{ memoryDate }}</span>
            </small>
          </div>
          <v-btn
            icon
            :aria-label="$t(&quot;Close record&quot;)"
            @click="dialogOpen = false"
          >
            <v-icon>{{ mdiClose }}</v-icon>
          </v-btn>
        </header>
        <div
          class="public-record-body"
          :class="{ 'public-record-body--text': !media.length }"
        >
          <div
            v-if="media.length"
            ref="mediaStage"
            class="public-record-media"
          >
            <button
              v-if="!currentMedia.video && !failedImages[currentMedia.url]"
              type="button"
              class="public-record-image-button"
              :aria-label="$t(&quot;Open image fullscreen&quot;)"
              @click="openViewer"
            >
              <img
                :src="currentMedia.url"
                :alt="displayName || $t(&quot;Memory image&quot;)"
                :style="imageStyle(currentMedia, mediaSize)"
                @error="$set(failedImages, currentMedia.url, true)"
              />
            </button>
            <ListVideo
              v-else-if="currentMedia.video"
              :key="currentMedia.url"
              controls
              playsinline
              preload="metadata"
              :poster="videoPosterUrl(currentMedia.url, record)"
              class="public-record-video"
            >
              <source
                :src="videoPreviewUrl(currentMedia.url)"
                :type="currentMedia.mime"
              >
            </ListVideo>
            <a
              v-else
              :href="currentMedia.url"
              target="_blank"
              rel="noopener noreferrer"
              class="public-record-file"
            >{{ $t("Open file") }}</a>
            <template v-if="media.length > 1">
              <v-btn
                icon
                dark
                class="public-record-arrow public-record-arrow--previous"
                :aria-label="$t(&quot;Previous media&quot;)"
                @click="moveMedia(-1)"
              >
                <v-icon>{{ mdiChevronLeft }}</v-icon>
              </v-btn>
              <v-btn
                icon
                dark
                class="public-record-arrow public-record-arrow--next"
                :aria-label="$t(&quot;Next media&quot;)"
                @click="moveMedia(1)"
              >
                <v-icon>{{ mdiChevronRight }}</v-icon>
              </v-btn>
              <span class="public-record-counter">{{ mediaIndex + 1 }} / {{ media.length }}</span>
            </template>
            <div
              class="public-record-like"
              :class="{ 'public-record-like--video': currentMedia.video }"
            >
              <v-btn
                icon
                dark
                :aria-label="liked ? $t(&quot;Unlike&quot;) : $t(&quot;Like&quot;)"
                :aria-pressed="liked"
                @click.stop="likeRecord"
              >
                <v-icon :color="liked ? 'red' : 'white'">
                  {{ liked ? mdiHeart : mdiHeartOutline }}
                </v-icon>
              </v-btn>
              <button
                v-if="likeCount"
                type="button"
                class="public-record-like-count"
                :aria-label="$t(&quot;View likes&quot;)"
                @click.stop="likersOpen = true"
              >
                {{ likeCount }}
              </button>
            </div>
          </div>
          <div class="public-record-information">
            <v-btn
              v-if="record.translation && record.translation.translated"
              small
              text
              color="primary"
              :aria-pressed="showOriginal"
              @click="showOriginal = !showOriginal"
            >
              {{ $t(showOriginal ? 'View translation' : 'View original') }}
            </v-btn>
            <div
              v-if="displayDescription"
              class="public-record-description"
              v-html="displayDescription"
            ></div>
            <div
              v-if="!media.length"
              class="pa-4"
            >
              <v-btn
                text
                :aria-pressed="liked"
                @click="likeRecord"
              >
                <v-icon
                  left
                  :color="liked ? 'red' : undefined"
                >
                  {{ liked ? mdiHeart : mdiHeartOutline }}
                </v-icon>
                {{ likeCount || '' }}{{ $t(" Like\n              ") }}
              </v-btn>
            </div>
            <section
              class="public-record-comments"
              :aria-label="$t(&quot;Comments&quot;)"
            >
              <h3 class="public-record-comments-title">
                {{ $t("\n                Comments ") }}<span v-if="commentCount">({{ commentCount }})</span>
              </h3>
              <CommentsSection
                v-if="record.id"
                :key="record.id"
                :file-id="record.id"
                fill-height
                @count-updated="commentCount = $event"
              />
              <router-link
                v-if="!authenticated"
                to="/auth"
                class="public-record-signin"
              >
                {{ $t("\n                Sign in to comment\n              ") }}
              </router-link>
            </section>
          </div>
        </div>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="viewerOpen"
      fullscreen
      content-class="public-record-viewer"
    >
      <div
        v-if="viewerOpen"
        class="public-record-viewer-card"
      >
        <header class="public-record-viewer-header">
          <span>{{ viewerIndex + 1 }} / {{ images.length }}</span>
          <v-btn
            icon
            dark
            :aria-label="$t(&quot;Close fullscreen image&quot;)"
            @click="viewerOpen = false"
          >
            <v-icon>{{ mdiClose }}</v-icon>
          </v-btn>
        </header>
        <div
          ref="viewerStage"
          v-touch="{ left: () => moveViewer(1), right: () => moveViewer(-1) }"
          class="public-record-viewer-stage"
        >
          <img
            v-if="images[viewerIndex]"
            :src="images[viewerIndex].url"
            :alt="displayName || $t(&quot;Memory image&quot;)"
            :style="imageStyle(images[viewerIndex], viewerSize)"
          />
          <template v-if="images.length > 1">
            <v-btn
              icon
              dark
              class="public-record-arrow public-record-arrow--previous"
              :aria-label="$t(&quot;Previous image&quot;)"
              @click="moveViewer(-1)"
            >
              <v-icon>{{ mdiChevronLeft }}</v-icon>
            </v-btn>
            <v-btn
              icon
              dark
              class="public-record-arrow public-record-arrow--next"
              :aria-label="$t(&quot;Next image&quot;)"
              @click="moveViewer(1)"
            >
              <v-icon>{{ mdiChevronRight }}</v-icon>
            </v-btn>
          </template>
        </div>
      </div>
    </v-dialog>
    <LikersDialog
      v-if="record"
      v-model="likersOpen"
      :file-id="record.id"
    />
  </div>
</template>

<script>
import {
  mdiClose, mdiChevronLeft, mdiChevronRight, mdiHeart, mdiHeartOutline,
} from '@mdi/js'
import { Touch } from 'vuetify/lib/directives'
import { isVideoUrl, videoMimeType } from '../utils/mediaType'
import CommentsSection from '@/components/CommentsSection.vue'
import LikersDialog from '@/components/LikersDialog.vue'
import ListVideo from '@/components/ListVideo.vue'
import videoPreviewUrl, { videoPosterUrl } from '@/utils/videoPreview'

export default {
  name: 'PublicRecordDialog',
  components: { CommentsSection, LikersDialog, ListVideo },
  directives: { Touch },
  props: {
    value: { type: Boolean, default: false },
    record: { type: Object, default: null },
    liked: { type: Boolean, default: false },
    likeCount: { type: Number, default: 0 },
    ownerName: { type: String, default: '' },
    ownerAvatar: { type: String, default: '' },
  },
  data: () => ({
    defaultAvatar: require('@/assets/images/avatars/1.png'),
    mdiClose,
    mdiChevronLeft,
    mdiChevronRight,
    mdiHeart,
    mdiHeartOutline,
    mediaIndex: 0,
    showOriginal: false,
    viewerIndex: 0,
    viewerOpen: false,
    likersOpen: false,
    commentCount: 0,
    failedImages: {},
    mediaSize: { width: 0, height: 0 },
    viewerSize: { width: 0, height: 0 },
  }),
  computed: {
    displayName() { return this.record && (this.showOriginal ? this.record.original_name : this.record.name) || '' },
    displayDescription() { return this.record && (this.showOriginal ? this.record.original_alternative_text : this.record.alternative_text) || '' },
    dialogOpen: {
      get() { return this.value },
      set(open) { this.$emit('input', open) },
    },
    authenticated() { return !!this.$store.state.jwtToken },
    memoryDate() {
      const date = this.record && this.record.created_at ? new Date(this.record.created_at) : null

      return date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString(this.$locale(), {
        year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC',
      }).replace(',', '') : ''
    },
    media() {
      if (!this.record) return []
      const urls = Array.isArray(this.record.related_files) && this.record.related_files.length ? this.record.related_files : [this.record.url].filter(Boolean)

      return urls.map((url, sourceIndex) => {
        if (typeof url !== 'string' || !url) return null
        const video = isVideoUrl(url)
        const rotation = Number((this.record.rotate_position || [])[sourceIndex]) || 0

        return {
          url, video, rotation, mime: videoMimeType(url),
        }
      }).filter(Boolean).map((item, index) => ({ ...item, index }))
    },
    currentMedia() { return this.media[this.mediaIndex] || this.media[0] },
    images() { return this.media.filter(item => !item.video && !this.failedImages[item.url]) },
  },
  watch: {
    record: {
      immediate: true,
      handler(record, previous) {
        if (!previous || !record || record.id !== previous.id || record.url !== previous.url) this.resetRecord()
        else this.showOriginal = false
      },
    },
    value(open) {
      if (open) this.$nextTick(this.observeStages)
      else {
        this.viewerOpen = false
        this.pauseVideos()
        if (this.stageObserver) this.stageObserver.disconnect()
      }
    },
    viewerOpen(open) {
      if (open) {
        window.addEventListener('keydown', this.viewerKeydown, true)
        this.$nextTick(this.observeStages)
      } else {
        window.removeEventListener('keydown', this.viewerKeydown, true)
        if (this.images[this.viewerIndex]) this.mediaIndex = this.images[this.viewerIndex].index
        this.$nextTick(() => {
          const button = this.$refs.mediaStage && this.$refs.mediaStage.querySelector('.public-record-image-button')
          if (button) button.focus()
        })
      }
    },
  },
  beforeDestroy() {
    if (this.stageObserver) this.stageObserver.disconnect()
    window.removeEventListener('keydown', this.viewerKeydown, true)
    this.pauseVideos()
  },
  methods: {
    videoPreviewUrl,
    videoPosterUrl,
    resetRecord() {
      this.showOriginal = false
      this.viewerOpen = false
      this.likersOpen = false
      this.commentCount = 0
      this.failedImages = {}
      this.mediaIndex = Math.max(0, this.media.findIndex(item => item.url === (this.record && this.record.url)))
      this.$nextTick(this.observeStages)
    },
    observeStages() {
      if (this.stageObserver) this.stageObserver.disconnect()
      if (typeof ResizeObserver === 'undefined') return
      this.stageObserver = new ResizeObserver(entries => {
        entries.forEach(entry => {
          const key = entry.target === this.$refs.mediaStage ? 'mediaSize' : 'viewerSize'
          this[key] = { width: entry.contentRect.width, height: entry.contentRect.height }
        })
      })
      if (this.$refs.mediaStage) this.stageObserver.observe(this.$refs.mediaStage)
      if (this.$refs.viewerStage) this.stageObserver.observe(this.$refs.viewerStage)
    },
    imageStyle(item, size) {
      const quarterTurn = Math.abs(item.rotation % 2) === 1 && size.width && size.height

      return { width: quarterTurn ? `${size.height}px` : '100%', height: quarterTurn ? `${size.width}px` : '100%', transform: `translate(-50%, -50%) rotate(${item.rotation * 90}deg)` }
    },
    pauseVideos() {
      if (this.$refs.mediaStage) this.$refs.mediaStage.querySelectorAll('video').forEach(video => video.pause())
    },
    moveMedia(step) {
      this.pauseVideos()
      this.mediaIndex = (this.mediaIndex + step + this.media.length) % this.media.length
    },
    openViewer() {
      this.viewerIndex = this.images.findIndex(item => item.index === this.currentMedia.index)
      this.viewerOpen = this.viewerIndex >= 0
    },
    moveViewer(step) {
      this.viewerIndex = (this.viewerIndex + step + this.images.length) % this.images.length
    },
    viewerKeydown(event) {
      if (!['Escape', 'ArrowLeft', 'ArrowRight'].includes(event.key)) return
      event.preventDefault()
      event.stopImmediatePropagation()
      if (event.key === 'Escape') this.viewerOpen = false
      else this.moveViewer(event.key === 'ArrowLeft' ? -1 : 1)
    },
    likeRecord() {
      if (this.authenticated) this.$emit('like', this.record)
      else this.$router.push('/auth')
    },
  },
}
</script>

<style>
.v-dialog.public-record-dialog {
  width: calc(100% - 48px);
  height: 80vh;
  height: min(80dvh, 760px);
  max-height: calc(100vh - 48px);
  max-height: calc(100dvh - 48px);
  margin: 24px;
  overflow: hidden;
}
@media (max-width: 600px) {
  .v-dialog.public-record-dialog {
    width: calc(100% - 24px);
    height: 90vh;
    height: 90dvh;
    max-height: calc(100vh - 24px);
    max-height: calc(100dvh - 24px);
    margin: 12px;
  }
}
</style>
<style scoped lang="scss">
.public-record-card { display: flex; flex-direction: column; height: 100%; padding: 16px; overflow: hidden; }
.public-record-header { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 2fr) minmax(0, 1fr); align-items: center; gap: 16px; flex: 0 0 auto; padding: 8px 4px 12px; border-bottom: 1px solid rgba(128,128,128,.2); }
.public-record-owner { display: flex; align-items: center; gap: 10px; min-width: 0; text-align: left; }
.public-record-owner .v-avatar { flex-shrink: 0; }
.public-record-owner span { overflow-wrap: anywhere; }
.public-record-header > .v-btn { justify-self: end; }
.public-record-heading { min-width: 0; text-align: center; }
.public-record-heading h2 { font-size: 20px; line-height: 1.4; margin: 0; display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; overflow: hidden; overflow-wrap: anywhere; }
.public-record-body { display: grid; grid-template-columns: minmax(0, 1.15fr) minmax(320px, 1fr); gap: 16px; padding-top: 16px; flex: 1; min-height: 0; }
.public-record-body--text { grid-template-columns: minmax(0, 1fr); }
.public-record-media { position: relative; overflow: hidden; min-height: 0; border-radius: 6px; background: #151515; }
.public-record-image-button { position: absolute; inset: 0; width: 100%; height: 100%; cursor: zoom-in; }
.public-record-image-button:focus-visible { outline: 3px solid #9155fd; outline-offset: -3px; }
.public-record-image-button img, .public-record-viewer-stage img { position: absolute; top: 50%; left: 50%; display: block; max-width: none; max-height: none; margin: 0; object-fit: cover; object-position: center; }
.public-record-video { height: 100%; }
.public-record-video ::v-deep video { height: 100%; max-height: none; min-height: 0; object-fit: contain; }
.public-record-video ::v-deep .list-video__poster { margin: 0; }
.public-record-file { display: grid; height: 100%; place-items: center; color: #fff; }
.public-record-arrow { position: absolute; top: 50%; transform: translateY(-50%); background: rgba(0,0,0,.65); z-index: 2; }
.public-record-arrow--previous { left: 12px; }
.public-record-arrow--next { right: 12px; }
.public-record-counter { position: absolute; top: 16px; right: 16px; padding: 4px 10px; border-radius: 16px; color: #fff; background: rgba(0,0,0,.65); pointer-events: none; }
.public-record-like { position: absolute; bottom: 16px; left: 16px; z-index: 2; display: flex; align-items: center; border-radius: 24px; background: rgba(0,0,0,.7); color: #fff; }
.public-record-like--video { bottom: 64px; }
.public-record-like-count { min-width: 32px; min-height: 44px; padding: 0 12px 0 4px; }
.public-record-information { display: flex; flex-direction: column; min-width: 0; min-height: 0; }
.public-record-description { flex: 0 1 auto; max-height: 45%; padding: 20px; overflow-y: auto; overflow-wrap: anywhere; }
.public-record-description ::v-deep img { max-width: 100%; height: auto; }
.public-record-description ::v-deep video, .public-record-description ::v-deep iframe { max-width: 100%; }
.public-record-comments { display: flex; flex-direction: column; flex: 1; min-height: 160px; border-top: 1px solid rgba(128,128,128,.2); overflow: hidden; }
.public-record-comments-title { flex: 0 0 auto; font-size: 15px; padding: 16px 20px 8px; }
.public-record-signin { flex: 0 0 auto; padding: 12px 20px; }
.public-record-viewer-card { height: 100vh; height: 100dvh; display: flex; flex-direction: column; background: #101010; color: #fff; }
.public-record-viewer-header { display: flex; align-items: center; justify-content: space-between; flex: 0 0 56px; padding: 0 12px 0 20px; }
.public-record-viewer-stage { position: relative; flex: 1; min-height: 0; overflow: hidden; touch-action: pan-y; }
.public-record-viewer-stage img { object-fit: contain; }
@media (max-width: 767px) {
  .public-record-card { padding: 12px; }
  .public-record-header { grid-template-columns: minmax(0, 1fr) auto; gap: 10px; padding: 4px 0 10px; }
  .public-record-heading { grid-column: 1 / -1; grid-row: 2; }
  .public-record-heading h2 { font-size: 18px; }
  .public-record-body { display: flex; flex-direction: column; overflow-y: auto; }
  .public-record-media { flex: 0 0 36vh; flex-basis: 36dvh; min-height: 200px; }
  .public-record-information { flex: 1 0 auto; min-height: 300px; }
  .public-record-description { max-height: none; flex: 0 0 auto; padding: 16px; }
  .public-record-comments { flex: 1 0 240px; }
}
</style>
